import { Badge } from '../ui/badge';
import { Download } from 'lucide-react';
import { Button } from '../ui/button';
import { useEffect } from 'react';

const GradebookTemplate = ({ gradebook, pathwayTitle, userName, autoDownload = false }) => {
    useEffect(() => {
        if (autoDownload && gradebook && gradebook.length > 0) {
            // Small delay to ensure the component is fully rendered before printing
            const timer = setTimeout(() => {
                window.print();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [autoDownload, gradebook]);

    const getGradeFromPercentage = percentage => {
        if (percentage === null || percentage === undefined) return '';
        if (percentage >= 80) return 'DISTINCTION';
        if (percentage >= 60) return 'MERIT';
        if (percentage >= 40) return 'PASS';
        return 'In Progress';
    };

    const getGradeBadgeColor = percentage => {
        if (percentage >= 80) return 'bg-success text-white';
        if (percentage >= 60) return 'bg-blue-600 text-white';
        if (percentage >= 40) return 'bg-yellow-400 text-white';
        return 'bg-red-600 text-white';
    };

    const getGradingScale = () => [
        { range: 'DISTINCTION', percentage: '80-100%' },
        { range: 'MERIT', percentage: '60-79%' },
        { range: 'PASS', percentage: '40-59%' },
    ];

    const calculatePathwayProgress = () => {
        if (!gradebook || gradebook.length === 0) return 0;
        let sum = 0;
        let count = 0;
        gradebook.forEach(course => {
            const progress = getNormalizedProgress(course);
            const complete = progress != null ? Math.round(progress) === 100 : isLikelyComplete(course);
            if (!complete) return;
            const score = course.user_score || 0;
            const total = course.total_score || 0;
            if (total > 0) {
                sum += (score / total) * 100;
                count += 1;
            }
        });
        if (count === 0) return 0;
        return Math.round(sum / count);
    };

    const getCompletedCoursesCount = () => {
        if (!gradebook || gradebook.length === 0) return 0;
        return gradebook.reduce((count, course) => {
            const progress = getNormalizedProgress(course);
            const complete = progress != null ? Math.round(progress) === 100 : isLikelyComplete(course);
            return count + (complete ? 1 : 0);
        }, 0);
    };

    const getNormalizedProgress = course => {
        const val =
            course?.progress ??
            course?.progress_percent ??
            course?.completion ??
            course?.course_progress ??
            course?.completed ??
            null;
        if (val === null || val === undefined) return null;
        if (typeof val === 'boolean') return val ? 100 : 0;
        const n = Number(val);
        if (Number.isNaN(n)) return null;
        return n <= 1 ? n * 100 : n;
    };

    const isLikelyComplete = course => {
        // Check if the course has a progress field that indicates 100% completion
        const progress = getNormalizedProgress(course);
        if (progress != null && Math.round(progress) === 100) return true;
        return false;
    };

    const isCourseComplete = course => {
        const progress = getNormalizedProgress(course);
        return progress != null && Math.round(progress) === 100;
    };

    if (!gradebook || gradebook.length === 0) {
        return (
            <div className="w-full max-w-4xl mx-auto p-6 bg-neutral-50 rounded-lg border border-neutral-300">
                <p className="text-center text-neutral-600">No gradebook data available.</p>
            </div>
        );
    }

    const pathwayProgress = calculatePathwayProgress();
    const completedCourses = getCompletedCoursesCount();
    const totalCourses = gradebook.length;

    return (
        <>
            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                    }
                    * {
                        page-break-inside: avoid;
                    }
                    .gradebook-container,
                    .gradebook-container * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        color-adjust: exact !important;
                    }
                    .gradebook-container {
                        padding: 0 !important;
                        max-width: 100% !important;
                        border: none !important;
                        border-radius: 6px !important;
                        background: white !important;
                        overflow: hidden !important;
                    }
                    .gradebook-container .bg-success { background-color: #10b981 !important; color: #fff !important; }
                    .gradebook-container .bg-blue-600 { background-color: #2563eb !important; color: #fff !important; }
                    .gradebook-container .bg-yellow-400 { background-color: #f59e0b !important; color: #fff !important; }
                    .gradebook-container .bg-red-600 { background-color: #dc2626 !important; color: #fff !important; }
                    .gradebook-container .rounded-full,
                    .gradebook-container [class*="rounded-full"] {
                        border-radius: 9999px !important;
                        display: inline-block !important;
                        overflow: visible !important;
                    }
                    .gradebook-container table {
                        border-collapse: collapse !important;
                    }
                    .gradebook-container table tbody tr {
                        border-bottom: 1px solid #d1d5db !important;
                    }
                    .gradebook-container table tbody tr:last-child {
                        border-bottom: none !important;
                    }
                    .gradebook-container table thead tr {
                        border-bottom: 2px solid #d1d5db !important;
                    }
                }
            `}</style>
            <div className="gradebook-container w-full max-w-4xl mx-auto bg-neutral-50 rounded-lg p-8">
                {/* ===== LETTERHEAD ===== */}
                <div className="mb-8 pb-6 border-b-2 border-neutral-300">
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left: Pathway Transcript Info */}
                        <div>
                            <h2 className="text-xl font-bold text-neutral-900 mb-4">Pathway Transcript</h2>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-neutral-600">Issued On:</span>
                                    <span className="font-semibold text-neutral-900">
                                        {new Date().toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-neutral-600">Student Name:</span>
                                    <span className="font-semibold text-neutral-900 capitalize">{userName || 'Student'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-neutral-600">Pathway:</span>
                                    <span className="font-semibold text-neutral-900 capitalize">{pathwayTitle}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Logo & School Details */}
                        <div className="flex flex-col items-end">
                            <img src="/Zindua Favicon.png" alt="Zindua School Logo" className="w-16 h-16 mb-3" />
                            <div className="text-right text-sm">
                                <h3 className="font-bold text-neutral-900">Zindua School</h3>
                                <p className="text-neutral-600">info@zinduaschool.com</p>
                                <p className="text-neutral-600">Nairobi, Kenya</p>
                                <p className="text-neutral-600">www.zinduaschool.com</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== SECTION A: Transcript Summary & Overall Grade ===== */}
                <div className="mb-8">
                    <h3 className="text-sm font-bold text-neutral-700 uppercase mb-4 tracking-wide">Transcript Summary</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left: Summary Details */}
                        <div className="border border-neutral-300 rounded-lg p-4 bg-white">
                            <table className="w-full">
                                <tbody>
                                    <tr className="border-b border-neutral-200">
                                        <td className="py-3 text-sm text-neutral-600">Pathway Status</td>
                                        <td className="py-3 text-sm font-semibold text-right">
                                            <span className={pathwayProgress === 100 ? 'text-success' : 'text-amber-600'}>
                                                {pathwayProgress === 100 ? 'Completed' : 'In Progress'}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-neutral-200">
                                        <td className="py-3 text-sm text-neutral-600">Courses Complete</td>
                                        <td className="py-3 text-sm font-semibold text-neutral-900 text-right">
                                            {completedCourses}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 text-sm text-neutral-600">Total Courses</td>
                                        <td className="py-3 text-sm font-semibold text-neutral-900 text-right">{totalCourses}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Right: Overall Grade */}
                        <div className="border border-neutral-300 rounded-lg p-6 bg-white flex flex-col items-center justify-center">
                            <h4 className="text-sm text-neutral-600 uppercase tracking-wide mb-2">Overall Grade</h4>
                            <div className="text-4xl font-bold text-neutral-900 mb-2">
                                {completedCourses === 0 ? '—' : `${pathwayProgress}%`}
                            </div>
                            <Badge
                                className={`text-sm px-3 py-1 ${
                                    completedCourses === 0
                                        ? 'bg-neutral-300 text-neutral-700'
                                        : getGradeBadgeColor(pathwayProgress)
                                }`}
                            >
                                {completedCourses === 0 ? 'In Progress' : getGradeFromPercentage(pathwayProgress)}
                            </Badge>
                        </div>
                    </div>
                </div>

                {/* ===== SECTION B: Course Breakdown ===== */}
                <div className="mb-8">
                    <h3 className="text-sm font-bold text-neutral-700 uppercase mb-4 tracking-wide">Course Breakdown</h3>
                    <div className="border border-neutral-300 rounded-lg overflow-hidden bg-white">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-neutral-100 border-b-2 border-neutral-300">
                                    <th className="text-left p-3 text-sm font-semibold text-neutral-900">Slug</th>
                                    <th className="text-left p-3 text-sm font-semibold text-neutral-900">Course Name</th>
                                    <th className="text-right p-3 text-sm font-semibold text-neutral-900">Score %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gradebook.map((course, idx) => {
                                    const userScore = course.user_score || 0;
                                    const totalScore = course.total_score || 0;
                                    const percentage = totalScore > 0 ? (userScore / totalScore) * 100 : 0;
                                    const isComplete = isCourseComplete(course);
                                    // Use course_slug from API, fallback to slug field
                                    const slug = course.course_slug || course.slug || '—';
                                    return (
                                        <tr
                                            key={course.course_id}
                                            className={idx !== gradebook.length - 1 ? 'border-b border-neutral-200' : ''}
                                        >
                                            <td className="p-3 text-sm text-neutral-500 font-mono">{slug}</td>
                                            <td className="p-3 text-sm text-neutral-800 font-medium">{course.course_title}</td>
                                            <td className="p-3 text-sm text-neutral-900 font-bold text-right">
                                                {isComplete ? (
                                                    `${percentage.toFixed(1)}%`
                                                ) : (
                                                    <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                                        In Progress
                                                    </Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ===== SECTION C: Grading Scale & School Confirmation ===== */}
                <div className="mb-8">
                    <h3 className="text-sm font-bold text-neutral-700 uppercase mb-4 tracking-wide">
                        Grading Scale & Confirmation
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left: Grading Scale */}
                        <div className="border border-neutral-300 rounded-lg p-4 bg-white">
                            <h4 className="text-sm font-semibold text-neutral-900 mb-3">Grading Scale</h4>
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-neutral-300">
                                        <th className="text-left pb-2 text-sm font-medium text-neutral-600">Grade</th>
                                        <th className="text-right pb-2 text-sm font-medium text-neutral-600">Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getGradingScale().map((item, idx) => (
                                        <tr
                                            key={idx}
                                            className={idx !== getGradingScale().length - 1 ? 'border-b border-neutral-100' : ''}
                                        >
                                            <td className="py-2 text-sm font-semibold text-neutral-800">{item.range}</td>
                                            <td className="py-2 text-sm text-neutral-600 text-right">{item.percentage}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Right: School Confirmation */}
                        <div className="border border-neutral-300 rounded-lg p-4 bg-white">
                            <h4 className="text-sm font-semibold text-neutral-900 mb-3">School Confirmation</h4>
                            <div className="py-4 text-sm">
                                <p className="text-neutral-600">
                                    This transcript is an official record of the student&apos;s academic performance at Zindua
                                    School.
                                </p>
                                <div className="pt-4 border-t border-neutral-200">
                                    <p className="text-neutral-500 text-xs">Authorized Signature</p>
                                    <div className="mt-6 border-b border-neutral-400 w-3/4"></div>
                                    <p className="text-neutral-600 mt-2 text-xs">Zindua School Administration</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Download Action */}
                <div className="flex justify-center print:hidden">
                    <Button variant="outline" className="flex items-center gap-2" onClick={() => window.print()}>
                        <Download size={18} />
                        Download Transcript
                    </Button>
                </div>

                {/* Auto-generated Footer */}
                <div className="hidden print:block text-center text-xs italic text-neutral-500 mt-8 pt-6 border-t border-neutral-200">
                    <p>
                        This transcript was autogenerated from{' '}
                        {typeof window !== 'undefined' ? window.location.host : 'Zindua LMS'} on {new Date().toLocaleString()} for{' '}
                        {userName}
                    </p>
                </div>
            </div>
        </>
    );
};

export default GradebookTemplate;
